import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getCookie, setCookie } from 'typescript-cookie';

import { useUser } from '@/contexts/UserContext';
import {
  BANIB_INFO_LINK,
  LADOBOM_REDIRECT,
  APP_CLIENT_ACCOUNT_URL,
  LADOBOM_ACCOUNT_CHECK_ENDPOINT,
  APP_REDE_SEGUROS_URL,
} from '@/config';
import getDivergencesPanelLink from '@/helpers/get-divergences-panel-link';

import { excludedClientDocuments } from './constants';

import bannerSignatrue from '@/images/banner-assinatura-rv.png';
import bannerBanib from '@/images/banner-banib.png';
import bannerContestationsClient from '@/images/banner-contestacoes-client.png';
import bannerLadoBom from '@/images/banner-ladobom.png';
import bannerPdi from '@/images/banner-pdi.png';
import bannerPlans from '@/images/banner-planos.png';
import bannerReparoFacil from '@/images/banner-reparofacil.png';
import bannerTooSeguros from '@/images/banner-too-seguros.png';

const NewRvPanelFeatureBar = ({ clientDocument }) => {
  const { profile, features } = useUser();
  const [ladoBomAllowed, setLadoBomAllowed] = useState(undefined);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [fade, setFade] = useState(true);

  const isBR = useMemo(() => !profile?.country || profile?.country === 'BR', [profile?.country]);
  const shouldExcludeLadoBom = useMemo(() => excludedClientDocuments.includes(clientDocument), [clientDocument]);

  // Checks Lado Bom permission (only for BR users)
  useEffect(() => {
    if (!isBR) return;

    if (shouldExcludeLadoBom) {
      setLadoBomAllowed(false);
      return;
    }

    const cookieValue = getCookie('ladoBomAllowed');
    if (cookieValue !== undefined && cookieValue !== '') {
      setLadoBomAllowed(cookieValue === 'true');
      return;
    }

    const accessToken = sessionStorage.getItem('accessToken');
    if (!accessToken) {
      setLadoBomAllowed(true);
      setCookie('ladoBomAllowed', 'true', { expires: 1 });
      return;
    }

    fetch(LADOBOM_ACCOUNT_CHECK_ENDPOINT, {
      method: 'GET',
      headers: {
        Authorization: accessToken,
      },
    })
      .then(response => {
        const allowed = !response.ok;
        setLadoBomAllowed(allowed);
        setCookie('ladoBomAllowed', allowed ? 'true' : 'false', { expires: 1 });
      })
      .catch(() => {
        setLadoBomAllowed(true);
        setCookie('ladoBomAllowed', 'true', { expires: 1 });
      });
  }, [isBR, shouldExcludeLadoBom]);

  // Dynamically builds banners list
  const banners = useMemo(() => {
    const contestationsBanner = {
      img: bannerContestationsClient,
      onClick: getDivergencesPanelLink,
    };

    // Users outside of BR see the contestations announcement and account banners
    if (!isBR) {
      return [
        contestationsBanner,
        { img: bannerPdi, url: `${APP_CLIENT_ACCOUNT_URL}/inspections`, isClientAccount: true },
        { img: bannerPlans, url: `${APP_CLIENT_ACCOUNT_URL}/company/plan`, isClientAccount: true },
      ];
    }

    const hasReparoFacilFranchisee = profile?.franchisee_features?.includes('ORDER_MARKETPLACE_REPARO_FACIL');
    const hasReparoFacilClient = features?.includes('VISTORIA_REPARO_FACIL');

    const list = [
      contestationsBanner,
      { img: bannerPdi, url: `${APP_CLIENT_ACCOUNT_URL}/inspections`, isClientAccount: true },
      ...(hasReparoFacilFranchisee && hasReparoFacilClient
        ? [{ img: bannerReparoFacil, url: 'https://www.reparofacil.imb.br/' }]
        : []),
      { img: bannerSignatrue, url: 'https://s.vistori.as/tutorial_assinatura_rv_20251118.pdf' },
      { img: bannerTooSeguros, url: APP_REDE_SEGUROS_URL, isClientAccount: true },
      { img: bannerPlans, url: `${APP_CLIENT_ACCOUNT_URL}/company/plan`, isClientAccount: true },
      ...(!shouldExcludeLadoBom && ladoBomAllowed !== false
        ? [{ img: bannerLadoBom, url: LADOBOM_REDIRECT }]
        : []),
      { img: bannerBanib, url: BANIB_INFO_LINK },
    ];

    return list;
  }, [isBR, shouldExcludeLadoBom, ladoBomAllowed, profile, features]);

  // Ensure current banner index is valid if list changes
  useEffect(() => {
    if (currentBanner >= banners.length) {
      setCurrentBanner(0);
    }
  }, [banners.length, currentBanner]);

  // Rotate banners
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setFade(false);
        setTimeout(() => {
          setCurrentBanner(prevBanner => (prevBanner + 1) % banners.length);
          setFade(true);
        }, 300);
      }, 45000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const handleRedirectToClientAccountApp = appUrl => {
    const accessToken = sessionStorage.getItem('accessToken');
    const tokenType = sessionStorage.getItem('tokenType');
    window.open(`${appUrl}#access_token=${accessToken}&token_type=${tokenType}&expires_in=`, '_blank');
  };

  const handleRedirectToApp = event => {
    const activeBanner = banners[currentBanner];
    if (!activeBanner) return;

    if (activeBanner.onClick) {
      activeBanner.onClick();
      return;
    }

    if (activeBanner.isClientAccount) {
      handleRedirectToClientAccountApp(activeBanner.url);
      return;
    }
    event.preventDefault();
    window.open(activeBanner.url, '_blank');
  };

  // Prevents rendering before knowing if the banner can be shown (only for BR users)
  if (isBR && ladoBomAllowed === undefined && !shouldExcludeLadoBom) {
    return null;
  }

  if (banners.length === 0) {
    return null;
  }

  const hasMultipleBanners = banners.length > 1;
  const opacityClass = hasMultipleBanners ? (fade ? 'opacity-100' : 'opacity-0') : 'opacity-100';

  return (
    <div>
      <img
        onClick={handleRedirectToApp}
        className={`w-full max-w-full cursor-pointer transition-opacity duration-500 ease-in-out ${opacityClass}`}
        src={banners[currentBanner]?.img}
        alt="Grupo RV"
      />
    </div>
  );
};

NewRvPanelFeatureBar.propTypes = {
  clientDocument: PropTypes.string.isRequired,
};

export default NewRvPanelFeatureBar;
